import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useUpdateSchedule } from "@/lib/queries"
import type { QuizScheduleSummary } from "@/lib/types"

const WEEK_OPTIONS = [ 1, 2, 3, 4 ]
const NO_DUE = "none"
const CUSTOM = "custom"

// The select value for a stored lead time. Anything that is not one of the
// week presets shows as a custom day count, so it is never rounded on save.
function dueValue(dueLeadDays: number | null): string {
  if (dueLeadDays === null) return NO_DUE
  const weeks = dueLeadDays / 7
  return WEEK_OPTIONS.includes(weeks) ? String(weeks) : CUSTOM
}

function parseDay(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number)
  return new Date(year, month - 1, day)
}

// Months added the way the server adds them: a day past the end of the
// target month (Aug 31 + 6 months) lands on that month's last day.
function addMonths(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1)
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(date.getDate(), lastDay))
  return target
}

// Mirrors the server: a changed interval opens the next round one interval
// after the current round opened, but never earlier than tomorrow.
function nextOpensOn(currentOpenedOn: string, months: number): Date {
  const now = new Date()
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
  const next = addMonths(parseDay(currentOpenedOn), months)
  return next > tomorrow ? next : tomorrow
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
}

// Edits how a repeating schedule opens future rounds. Rounds already open
// keep their own dates.
export function ScheduleDialog({
  schedule,
  currentOpenedOn,
  open,
  onOpenChange,
}: {
  schedule: QuizScheduleSummary
  currentOpenedOn: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const updateSchedule = useUpdateSchedule()
  const [cycle, setCycle] = useState(String(schedule.interval_months))
  const { interval_months, due_lead_days } = schedule
  const savedDue = dueValue(due_lead_days)
  const [due, setDue] = useState(savedDue)

  // Start from the saved schedule each time the dialog opens. Keyed on the
  // saved values, not the object, so a background refetch keeps edits.
  useEffect(() => {
    if (!open) return
    setCycle(String(interval_months))
    setDue(dueValue(due_lead_days))
  }, [ open, interval_months, due_lead_days ])

  const months = cycle === "6" ? 6 : 12
  const nextOpens =
    months === interval_months
      ? parseDay(schedule.next_opens_on)
      : nextOpensOn(currentOpenedOn, months)

  const handleSave = async () => {
    try {
      await updateSchedule.mutateAsync({
        id: schedule.id,
        interval_months: months,
        // Only when the due selection changed: the stored lead stays exact.
        ...(due !== savedDue && {
          due_in_weeks: due === NO_DUE ? null : Number(due),
        }),
      })
      toast.success("Schedule updated")
      onOpenChange(false)
    } catch {
      toast.error("Failed to update schedule")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit schedule</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor={`schedule-cycle-${schedule.id}`}>Repeats</Label>
            <Select value={cycle} onValueChange={setCycle}>
              <SelectTrigger id={`schedule-cycle-${schedule.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">Every 6 months</SelectItem>
                <SelectItem value="12">Every year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`schedule-due-${schedule.id}`}>Due</Label>
            <Select value={due} onValueChange={setDue}>
              <SelectTrigger id={`schedule-due-${schedule.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {savedDue === CUSTOM && (
                  <SelectItem value={CUSTOM}>
                    {due_lead_days} {due_lead_days === 1 ? "day" : "days"} (custom)
                  </SelectItem>
                )}
                {WEEK_OPTIONS.map((weeks) => (
                  <SelectItem key={weeks} value={String(weeks)}>
                    {weeks === 1 ? "1 week" : `${weeks} weeks`} after it opens
                  </SelectItem>
                ))}
                <SelectItem value={NO_DUE}>No due date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-muted-foreground">
            Next round opens {formatDate(nextOpens)}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateSchedule.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
